/**
 * Multi-Step Client-Onboarding-Form.
 *
 * 3 Steps: Firma → Personen+Billing → Operativ.
 * Dynamic field-arrays für Geschäftsführer, Zeichnungsberechtigte und
 * Vor-Ort-Kontakte (`useFieldArray`).
 */
import {
  upsertClientSchema,
  type UpsertClientInput,
} from '@hirebotiq-ops/core';
import { zodResolver } from '@hookform/resolvers/zod';
import { useEffect, useState } from 'react';
import { useFieldArray, useForm } from 'react-hook-form';
import { toast } from 'sonner';
import { trpc } from '../../lib/trpc.js';
import { Button } from '../ui/button.js';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../ui/card.js';
import { Field } from '../ui/field.js';
import { Input } from '../ui/input.js';
import { Textarea } from '../ui/textarea.js';

type FormValues = UpsertClientInput;

const STEPS = [
  { id: 1, title: 'Firma', description: 'Rechtliche Daten und Anschrift' },
  {
    id: 2,
    title: 'Personen & Billing',
    description: 'Geschäftsführung, Zeichnungsberechtigte, Rechnungsadresse',
  },
  { id: 3, title: 'Einsatzbetrieb', description: 'Zeiterfassung und Vor-Ort-Kontakte' },
] as const;

const stripEmpty = (input: Record<string, unknown>): Record<string, unknown> => {
  const out: Record<string, unknown> = {};
  for (const [k, v] of Object.entries(input)) {
    if (v === '' || v === undefined || v === null) continue;
    if (Array.isArray(v) && v.length === 0) continue;
    out[k] = v;
  }
  return out;
};

export function ClientOnboardingForm() {
  const utils = trpc.useUtils();
  const profile = trpc.clients.getMine.useQuery();
  const upsert = trpc.clients.upsertMine.useMutation({
    onSuccess: () => utils.clients.getMine.invalidate(),
  });
  const submit = trpc.clients.submit.useMutation({
    onSuccess: () => {
      toast.success('Firmenprofil eingereicht.');
      utils.clients.getMine.invalidate();
    },
    onError: (err) => toast.error(err.message),
  });

  const [step, setStep] = useState(1);

  const form = useForm<FormValues>({
    resolver: zodResolver(upsertClientSchema),
    defaultValues: {
      managingDirectors: [],
      authorizedSignatories: [],
      onsiteContacts: [],
    },
    mode: 'onBlur',
  });

  const directors = useFieldArray({ control: form.control, name: 'managingDirectors' });
  const signatories = useFieldArray({ control: form.control, name: 'authorizedSignatories' });
  const onsite = useFieldArray({ control: form.control, name: 'onsiteContacts' });

  useEffect(() => {
    if (!profile.data) return;
    const c = profile.data;
    form.reset({
      companyName: c.companyName ?? undefined,
      legalForm: c.legalForm ?? undefined,
      registeredAddressStreet: c.registeredAddressStreet ?? undefined,
      registeredAddressZip: c.registeredAddressZip ?? undefined,
      registeredAddressCity: c.registeredAddressCity ?? undefined,
      hrbNumber: c.hrbNumber ?? undefined,
      districtCourt: c.districtCourt ?? undefined,
      vatId: c.vatId ?? undefined,
      managingDirectors: (c.managingDirectors as { name: string }[] | null) ?? [],
      authorizedSignatories:
        (c.authorizedSignatories as FormValues['authorizedSignatories']) ?? [],
      billingEmail: c.billingEmail ?? undefined,
      billingAddressDifferent: c.billingAddressDifferent ?? false,
      billingAddressStreet: c.billingAddressStreet ?? undefined,
      billingAddressZip: c.billingAddressZip ?? undefined,
      billingAddressCity: c.billingAddressCity ?? undefined,
      requirePoNumber: c.requirePoNumber ?? false,
      requireCostCenter: c.requireCostCenter ?? false,
      paymentTermsDays: c.paymentTermsDays ?? undefined,
      earlyPaymentDiscount: c.earlyPaymentDiscount ?? undefined,
      timeTrackingMethod: c.timeTrackingMethod ?? undefined,
      onsiteContacts: (c.onsiteContacts as FormValues['onsiteContacts']) ?? [],
    });
  }, [profile.data, form]);

  const readOnly =
    profile.data?.status === 'approved' ||
    profile.data?.status === 'pending_review' ||
    profile.data?.status === 'blocked';

  const handleNext = async () => {
    const v = form.getValues();
    try {
      await upsert.mutateAsync(stripEmpty(v) as FormValues);
      toast.success('Zwischenstand gespeichert.');
      if (step < STEPS.length) setStep(step + 1);
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'Fehler beim Speichern');
    }
  };

  const handleSubmit = async () => {
    const v = form.getValues();
    try {
      await upsert.mutateAsync(stripEmpty(v) as FormValues);
      await submit.mutateAsync();
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'Fehler beim Submit');
    }
  };

  if (profile.isLoading) {
    return <div className="text-center text-[var(--muted-foreground)] py-12">Lade Profil …</div>;
  }

  return (
    <div className="space-y-6">
      <header className="space-y-1">
        <p className="text-xs uppercase tracking-wider text-[var(--muted-foreground)]">
          Schritt {step} von {STEPS.length}
        </p>
        <h1 className="text-2xl font-bold">{STEPS[step - 1].title}</h1>
        <p className="text-sm text-[var(--muted-foreground)]">{STEPS[step - 1].description}</p>
      </header>

      {readOnly && (
        <Card>
          <CardContent className="pt-6">
            <p className="text-sm text-[var(--muted-foreground)]">
              Status: <span className="font-mono">{profile.data?.status}</span>. Bearbeitung nur
              in <span className="font-mono">draft</span> oder{' '}
              <span className="font-mono">rejected</span> möglich.
            </p>
          </CardContent>
        </Card>
      )}

      <Card>
        <CardHeader>
          <CardTitle>{STEPS[step - 1].title}</CardTitle>
          <CardDescription>{STEPS[step - 1].description}</CardDescription>
        </CardHeader>
        <CardContent>
          <form className="space-y-6">
            {step === 1 && <Step1 form={form} disabled={readOnly} />}
            {step === 2 && (
              <Step2
                form={form}
                disabled={readOnly}
                directors={directors}
                signatories={signatories}
              />
            )}
            {step === 3 && <Step3 form={form} disabled={readOnly} onsite={onsite} />}
          </form>
        </CardContent>
      </Card>

      <div className="flex justify-between">
        <Button
          variant="outline"
          onClick={() => setStep(Math.max(1, step - 1))}
          disabled={step === 1}
        >
          Zurück
        </Button>
        {step < STEPS.length ? (
          <Button onClick={handleNext} disabled={readOnly || upsert.isPending}>
            {upsert.isPending ? 'Speichere …' : 'Weiter'}
          </Button>
        ) : (
          <Button
            onClick={handleSubmit}
            disabled={readOnly || submit.isPending || upsert.isPending}
          >
            {submit.isPending ? 'Reiche ein …' : 'Profil einreichen'}
          </Button>
        )}
      </div>
    </div>
  );
}

type StepProps = { form: ReturnType<typeof useForm<FormValues>>; disabled?: boolean };

function Step1({ form, disabled }: StepProps) {
  const { register, formState } = form;
  return (
    <div className="grid gap-4 md:grid-cols-2">
      <Field
        label="Firmenname *"
        htmlFor="companyName"
        error={formState.errors.companyName?.message}
      >
        <Input id="companyName" disabled={disabled} {...register('companyName')} />
      </Field>
      <Field label="Rechtsform *" htmlFor="legalForm" hint="GmbH / AG / GbR">
        <Input id="legalForm" disabled={disabled} {...register('legalForm')} />
      </Field>
      <div className="md:col-span-2">
        <Field label="Sitz: Straße & Hausnummer *" htmlFor="registeredAddressStreet">
          <Input
            id="registeredAddressStreet"
            disabled={disabled}
            {...register('registeredAddressStreet')}
          />
        </Field>
      </div>
      <Field label="PLZ *" htmlFor="registeredAddressZip">
        <Input
          id="registeredAddressZip"
          disabled={disabled}
          {...register('registeredAddressZip')}
        />
      </Field>
      <Field label="Stadt *" htmlFor="registeredAddressCity">
        <Input
          id="registeredAddressCity"
          disabled={disabled}
          {...register('registeredAddressCity')}
        />
      </Field>
      <Field label="Handelsregister-Nr." htmlFor="hrbNumber" hint="z.B. HRB 12345">
        <Input id="hrbNumber" disabled={disabled} {...register('hrbNumber')} />
      </Field>
      <Field label="Amtsgericht" htmlFor="districtCourt">
        <Input id="districtCourt" disabled={disabled} {...register('districtCourt')} />
      </Field>
      <Field
        label="USt-ID *"
        htmlFor="vatId"
        error={formState.errors.vatId?.message}
        hint="z.B. DE123456789"
      >
        <Input id="vatId" disabled={disabled} {...register('vatId')} />
      </Field>
    </div>
  );
}

// Wiederverwendbare Steps für Staff-Anlegen/-Edit (Step2/3 brauchen die
// useFieldArray-Helper als Props — vom Aufrufer erzeugt). Function-Declarations
// sind gehoisted, daher hier vor ihrer Definition exportierbar.
export {
  Step1 as ClientLegalEntityStep,
  Step2 as ClientPeopleBillingStep,
  Step3 as ClientOperationalStep,
};

function Step2({
  form,
  disabled,
  directors,
  signatories,
}: StepProps & {
  directors: ReturnType<typeof useFieldArray<FormValues, 'managingDirectors'>>;
  signatories: ReturnType<typeof useFieldArray<FormValues, 'authorizedSignatories'>>;
}) {
  const { register, formState, watch, setValue } = form;
  const billingDifferent = watch('billingAddressDifferent');

  return (
    <div className="space-y-6">
      <section className="space-y-3">
        <div className="flex items-center justify-between">
          <h3 className="text-sm font-semibold">Geschäftsführung *</h3>
          <Button
            type="button"
            size="sm"
            variant="outline"
            disabled={disabled}
            onClick={() => directors.append({ name: '' })}
          >
            + Hinzufügen
          </Button>
        </div>
        {directors.fields.length === 0 && (
          <p className="text-xs text-[var(--muted-foreground)]">
            Mindestens einen Geschäftsführer ergänzen.
          </p>
        )}
        {directors.fields.map((field, idx) => (
          <div key={field.id} className="flex gap-2">
            <Input
              disabled={disabled}
              placeholder="Vor- und Nachname"
              {...register(`managingDirectors.${idx}.name` as const)}
            />
            <Button
              type="button"
              variant="outline"
              size="sm"
              disabled={disabled}
              onClick={() => directors.remove(idx)}
            >
              ×
            </Button>
          </div>
        ))}
      </section>

      <section className="space-y-3">
        <div className="flex items-center justify-between">
          <h3 className="text-sm font-semibold">Zeichnungsberechtigte</h3>
          <Button
            type="button"
            size="sm"
            variant="outline"
            disabled={disabled}
            onClick={() =>
              signatories.append({ name: '', function: '', email: '', phone: '' })
            }
          >
            + Hinzufügen
          </Button>
        </div>
        {signatories.fields.map((field, idx) => (
          <div key={field.id} className="grid gap-2 md:grid-cols-4">
            <Input
              disabled={disabled}
              placeholder="Name"
              {...register(`authorizedSignatories.${idx}.name` as const)}
            />
            <Input
              disabled={disabled}
              placeholder="Funktion"
              {...register(`authorizedSignatories.${idx}.function` as const)}
            />
            <Input
              disabled={disabled}
              type="email"
              placeholder="E-Mail"
              {...register(`authorizedSignatories.${idx}.email` as const)}
            />
            <div className="flex gap-2">
              <Input
                disabled={disabled}
                placeholder="Telefon"
                {...register(`authorizedSignatories.${idx}.phone` as const)}
              />
              <Button
                type="button"
                variant="outline"
                size="sm"
                disabled={disabled}
                onClick={() => signatories.remove(idx)}
              >
                ×
              </Button>
            </div>
          </div>
        ))}
      </section>

      <section className="space-y-3">
        <h3 className="text-sm font-semibold">Rechnungsadresse</h3>
        <div className="grid gap-4 md:grid-cols-2">
          <Field
            label="Rechnungs-E-Mail *"
            htmlFor="billingEmail"
            error={formState.errors.billingEmail?.message}
          >
            <Input
              id="billingEmail"
              type="email"
              disabled={disabled}
              {...register('billingEmail')}
            />
          </Field>
          <Field label="Zahlungsziel (Tage)" htmlFor="paymentTermsDays">
            <Input
              id="paymentTermsDays"
              type="number"
              min={0}
              disabled={disabled}
              {...register('paymentTermsDays', { valueAsNumber: true })}
            />
          </Field>
          <div className="md:col-span-2 flex flex-wrap gap-4">
            <label className="flex items-center gap-2 text-sm">
              <input
                type="checkbox"
                disabled={disabled}
                checked={billingDifferent ?? false}
                onChange={(e) => setValue('billingAddressDifferent', e.target.checked)}
                className="size-4"
              />
              Rechnungsanschrift abweichend
            </label>
            <label className="flex items-center gap-2 text-sm">
              <input
                type="checkbox"
                disabled={disabled}
                {...register('requirePoNumber')}
                className="size-4"
              />
              Bestellnummer auf Rechnung erforderlich
            </label>
            <label className="flex items-center gap-2 text-sm">
              <input
                type="checkbox"
                disabled={disabled}
                {...register('requireCostCenter')}
                className="size-4"
              />
              Kostenstelle erforderlich
            </label>
          </div>
          {billingDifferent && (
            <>
              <div className="md:col-span-2">
                <Field label="Rechnungs-Straße" htmlFor="billingAddressStreet">
                  <Input
                    id="billingAddressStreet"
                    disabled={disabled}
                    {...register('billingAddressStreet')}
                  />
                </Field>
              </div>
              <Field label="Rechnungs-PLZ" htmlFor="billingAddressZip">
                <Input
                  id="billingAddressZip"
                  disabled={disabled}
                  {...register('billingAddressZip')}
                />
              </Field>
              <Field label="Rechnungs-Stadt" htmlFor="billingAddressCity">
                <Input
                  id="billingAddressCity"
                  disabled={disabled}
                  {...register('billingAddressCity')}
                />
              </Field>
            </>
          )}
          <div className="md:col-span-2">
            <Field label="Skonto / Zahlungshinweise" htmlFor="earlyPaymentDiscount">
              <Input
                id="earlyPaymentDiscount"
                disabled={disabled}
                placeholder="z. B. 2% Skonto bei 14 Tagen"
                {...register('earlyPaymentDiscount')}
              />
            </Field>
          </div>
        </div>
      </section>
    </div>
  );
}

function Step3({
  form,
  disabled,
  onsite,
}: StepProps & {
  onsite: ReturnType<typeof useFieldArray<FormValues, 'onsiteContacts'>>;
}) {
  const { register } = form;
  return (
    <div className="space-y-6">
      <Field label="Zeiterfassung" htmlFor="timeTrackingMethod">
        <Textarea
          id="timeTrackingMethod"
          disabled={disabled}
          rows={3}
          placeholder='z. B. "Stundenzettel mit Unterschrift Schichtleiter"'
          {...register('timeTrackingMethod')}
        />
      </Field>

      <section className="space-y-3">
        <div className="flex items-center justify-between">
          <h3 className="text-sm font-semibold">Vor-Ort-Kontakte</h3>
          <Button
            type="button"
            size="sm"
            variant="outline"
            disabled={disabled}
            onClick={() =>
              onsite.append({ name: '', function: '', email: '', phone: '' })
            }
          >
            + Hinzufügen
          </Button>
        </div>
        <p className="text-xs text-[var(--muted-foreground)]">
          Personen, die Stundenzettel unterschreiben oder Worker einweisen.
        </p>
        {onsite.fields.map((field, idx) => (
          <div key={field.id} className="grid gap-2 md:grid-cols-4">
            <Input
              disabled={disabled}
              placeholder="Name"
              {...register(`onsiteContacts.${idx}.name` as const)}
            />
            <Input
              disabled={disabled}
              placeholder="Funktion"
              {...register(`onsiteContacts.${idx}.function` as const)}
            />
            <Input
              disabled={disabled}
              type="email"
              placeholder="E-Mail"
              {...register(`onsiteContacts.${idx}.email` as const)}
            />
            <div className="flex gap-2">
              <Input
                disabled={disabled}
                placeholder="Telefon"
                {...register(`onsiteContacts.${idx}.phone` as const)}
              />
              <Button
                type="button"
                variant="outline"
                size="sm"
                disabled={disabled}
                onClick={() => onsite.remove(idx)}
              >
                ×
              </Button>
            </div>
          </div>
        ))}
      </section>
    </div>
  );
}
